import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Share2, Download, Copy, Film, Mic, Sparkles, Heart, Megaphone,
  Trophy, Instagram, Twitter, Music as TikTokIcon, MessageCircle, Wand2, X,
  Facebook, Linkedin, Send, MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";

/**
 * One-stop social distribution surface. Aggregates every shareable
 * asset the couple has produced: compatibility cards, Get-Locked-In-style
 * announcements, TikTok cards/clips per game, AI reels, audio
 * blessings. Quick actions for download, copy-link, and "Share to
 * Instagram/TikTok" web-intent links.
 */
export default function ShareStudioPage() {
  const [data, setData] = useState({ wedding: null, games: [], reels: [], blessings: [], announcements: [], compatibility: null });
  const [tab, setTab] = useState("all");
  const [captionFor, setCaptionFor] = useState(null);
  const supportsShare = typeof navigator !== "undefined" && !!navigator.share;

  useEffect(() => { (async () => {
    try {
      const [w, games, reels, blessings, ann, comp] = await Promise.all([
        api.get("/weddings/mine").catch(() => ({ data: null })),
        api.get("/locked-in/games/me").catch(() => ({ data: [] })),
        api.get("/reels/mine").catch(() => ({ data: [] })),
        api.get("/blessings/mine").catch(() => ({ data: [] })),
        api.get("/announcements?wedding_only=true").catch(() => ({ data: [] })),
        api.get("/compatibility/share-card").catch(() => ({ data: null })),
      ]);
      setData({
        wedding: w.data,
        games: games.data || [],
        reels: reels.data || [],
        blessings: (blessings.data || []).filter((b) => b.is_approved),
        announcements: ann.data || [],
        compatibility: comp.data,
      });
    } catch (e) { /* noop */ }
  })(); }, []);

  function copyLink(url) {
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  }

  async function nativeShare(asset) {
    if (!navigator.share) { copyLink(asset.shareUrl); return; }
    try {
      await navigator.share({
        title: asset.title,
        text: `${asset.title} — Locked In Forever`,
        url: asset.shareUrl,
      });
    } catch (err) {
      // User cancelled — silent
      if (err?.name !== "AbortError") toast.error("Share failed");
    }
  }

  // Build flat list of assets
  const assets = [];
  data.games.forEach((g) => {
    assets.push({
      kind: "tiktok-png",
      title: `${g.title || "Game"} — TikTok Card`,
      subtitle: `Code ${g.code} · 1080×1920`,
      icon: MessageCircle,
      preview: `${BACKEND_URL}/api/public/play/${g.code}/tiktok-card.png`,
      download: `${BACKEND_URL}/api/public/play/${g.code}/tiktok-card.png`,
      shareUrl: `${window.location.origin}/play/${g.code}`,
      filename: `lockedin-${g.code}.png`,
    });
    assets.push({
      kind: "tiktok-mp4",
      title: `${g.title || "Game"} — TikTok Clip`,
      subtitle: `Code ${g.code} · 5s MP4`,
      icon: Film,
      preview: `${BACKEND_URL}/api/public/play/${g.code}/tiktok-card.png`,
      download: `${BACKEND_URL}/api/public/play/${g.code}/tiktok-clip.mp4`,
      videoUrl: `${BACKEND_URL}/api/public/play/${g.code}/tiktok-clip.mp4`,
      shareUrl: `${window.location.origin}/play/${g.code}`,
      filename: `lockedin-${g.code}.mp4`,
    });
  });
  data.reels.filter((r) => r.status === "ready").forEach((r) => {
    assets.push({
      kind: "reel",
      title: "Wedding Reel",
      subtitle: `${r.duration}s · ${r.size}`,
      icon: Film,
      videoUrl: `${BACKEND_URL}/api/files/${r.storage_path}`,
      download: `${BACKEND_URL}/api/files/${r.storage_path}`,
      shareUrl: data.wedding ? `${window.location.origin}/site/${data.wedding.slug}` : "",
      filename: `wedding-reel-${r.reel_id}.mp4`,
    });
  });
  if (data.compatibility) {
    assets.push({
      kind: "compat",
      title: "Compatibility Card",
      subtitle: `${data.compatibility.matched_type || "Couple"} · ${data.compatibility.score || 0}%`,
      icon: Heart,
      preview: `${BACKEND_URL}/api/compatibility/share-card`,
      download: `${BACKEND_URL}/api/compatibility/share-card`,
      shareUrl: data.wedding ? `${window.location.origin}/site/${data.wedding.slug}` : "",
      filename: "compatibility.png",
    });
  }
  data.announcements.forEach((a) => {
    assets.push({
      kind: "announcement",
      title: a.title || "Announcement",
      subtitle: new Date(a.created_at).toLocaleDateString(),
      icon: Megaphone,
      preview: `${BACKEND_URL}/api/announcements/${a.announcement_id}/share-card.png`,
      download: `${BACKEND_URL}/api/announcements/${a.announcement_id}/share-card.png`,
      shareUrl: `${window.location.origin}/announcements`,
      filename: `announcement-${a.announcement_id}.png`,
    });
  });
  data.blessings.forEach((b) => {
    assets.push({
      kind: "blessing",
      title: `${b.guest_name}'s Blessing`,
      subtitle: new Date(b.created_at).toLocaleDateString(),
      icon: Mic,
      audioUrl: `${BACKEND_URL}/api/files/${b.audio_path}`,
      download: `${BACKEND_URL}/api/files/${b.audio_path}`,
      shareUrl: data.wedding ? `${window.location.origin}/site/${data.wedding.slug}` : "",
      filename: `blessing-${b.guest_name}.webm`,
    });
  });

  const TABS = [
    { v: "all", l: "All" },
    { v: "tiktok-png", l: "TikTok Cards" },
    { v: "tiktok-mp4", l: "TikTok Clips" },
    { v: "reel", l: "AI Reels" },
    { v: "compat", l: "Compatibility" },
    { v: "announcement", l: "Announcements" },
    { v: "blessing", l: "Blessings" },
  ];
  const filtered = tab === "all" ? assets : assets.filter((a) => a.kind === tab);

  return (
    <div data-testid="share-studio">
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-1 flex items-center gap-2">
          <Share2 className="w-3 h-3" /> Share Studio
        </p>
        <h1 className="font-serif text-4xl md:text-5xl">Every <span className="font-script text-[var(--gold)]">moment</span>, ready to post.</h1>
        <p className="text-sm text-[var(--muted)] mt-2 max-w-2xl">
          Your share-ready assets in one place. Download, copy a link, or post straight to TikTok / Instagram / Twitter.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="share-studio-tabs">
        {TABS.map((t) => {
          const count = t.v === "all" ? assets.length : assets.filter((a) => a.kind === t.v).length;
          return (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`tab-${t.v}`}
              className={`text-xs px-4 py-2 rounded-full border transition-colors ${tab === t.v ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[var(--line)] text-[var(--muted)] hover:border-[var(--gold)]"}`}
            >
              {t.l} <span className="opacity-60 ml-1">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Assets grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[var(--line)] rounded-2xl" data-testid="empty-studio">
          <Sparkles className="w-10 h-10 mx-auto text-[var(--gold)] mb-3" />
          <p className="font-serif text-2xl">Nothing here yet</p>
          <p className="text-sm text-[var(--muted)] mt-2">Generate a Locked-In game, AI reel, or compatibility result and your assets will appear here.</p>
          <div className="flex justify-center gap-2 mt-6">
            <Link to="/dashboard/locked-in" className="btn-outline-gold text-xs">Locked In</Link>
            <Link to="/dashboard/reels" className="btn-outline-gold text-xs">Reels</Link>
            <Link to="/dashboard/compatibility" className="btn-outline-gold text-xs">Compatibility</Link>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((a, i) => (
            <AssetCard
              key={`${a.kind}-${i}`}
              asset={a}
              coupleNames={data.wedding?.couple_names}
              onCopyLink={copyLink}
              onNativeShare={supportsShare ? nativeShare : null}
              onCaption={setCaptionFor}
            />
          ))}
        </div>
      )}

      {captionFor && (
        <CaptionModal
          asset={captionFor}
          coupleNames={data.wedding?.couple_names}
          onClose={() => setCaptionFor(null)}
        />
      )}
    </div>
  );
}

function AssetCard({ asset, coupleNames, onCopyLink, onNativeShare, onCaption }) {
  const Icon = asset.icon || Sparkles;
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl overflow-hidden" data-testid={`asset-${asset.kind}`}>
      <div className="aspect-[9/16] bg-gradient-to-br from-[var(--ink)] to-black relative overflow-hidden">
        {asset.videoUrl ? (
          <video src={asset.videoUrl} controls playsInline poster={asset.preview} className="w-full h-full object-cover" />
        ) : asset.audioUrl ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6 text-center">
            <Mic className="w-16 h-16 mb-4 text-[var(--gold)]" />
            <audio controls src={asset.audioUrl} className="w-full" />
          </div>
        ) : (
          <img src={asset.preview} alt={asset.title} className="w-full h-full object-cover" />
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-3.5 h-3.5 text-[var(--gold)]" />
          <p className="font-medium text-sm truncate">{asset.title}</p>
        </div>
        <p className="text-[11px] text-[var(--muted)] mb-3">{asset.subtitle}</p>
        <div className="flex items-center gap-1 flex-wrap">
          <a
            href={asset.download}
            download={asset.filename}
            className="flex-1 text-xs btn-outline-gold py-2"
            data-testid={`download-${asset.kind}`}
          >
            <Download className="w-3 h-3" /> Download
          </a>
          <button
            onClick={() => onCaption(asset)}
            data-testid={`caption-${asset.kind}`}
            title="Generate AI caption"
            className="text-xs p-2 hover:text-[var(--gold)]"
          >
            <Wand2 className="w-3.5 h-3.5" />
          </button>
          {onNativeShare ? (
            <button
              onClick={() => onNativeShare(asset)}
              data-testid={`share-${asset.kind}`}
              title="Native share"
              className="text-xs p-2 hover:text-[var(--gold)]"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={() => onCopyLink(asset.shareUrl)}
              className="text-xs p-2 hover:text-[var(--gold)]"
              title="Copy share link"
              data-testid={`copy-${asset.kind}`}
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          )}
          <a
            href={`https://www.instagram.com/`}
            target="_blank"
            rel="noreferrer"
            className="text-xs p-2 hover:text-pink-500"
            title="Open Instagram"
            data-testid={`ig-${asset.kind}`}
          >
            <Instagram className="w-3.5 h-3.5" />
          </a>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(asset.title + " — ")}&url=${encodeURIComponent(asset.shareUrl || "")}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs p-2 hover:text-blue-500"
            title="Tweet"
            data-testid={`twitter-${asset.kind}`}
          >
            <Twitter className="w-3.5 h-3.5" />
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(asset.shareUrl || "")}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs p-2 hover:text-blue-700"
            title="Facebook"
            data-testid={`fb-${asset.kind}`}
          >
            <Facebook className="w-3.5 h-3.5" />
          </a>
          <a
            href={`https://wa.me/?text=${encodeURIComponent((asset.title || "") + " — " + (asset.shareUrl || ""))}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs p-2 hover:text-green-600"
            title="WhatsApp"
            data-testid={`wa-${asset.kind}`}
          >
            <Send className="w-3.5 h-3.5" />
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(asset.shareUrl || "")}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs p-2 hover:text-blue-800"
            title="LinkedIn"
            data-testid={`li-${asset.kind}`}
          >
            <Linkedin className="w-3.5 h-3.5" />
          </a>
          <a
            href={`https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(asset.shareUrl || "")}&description=${encodeURIComponent(asset.title || "")}&media=${encodeURIComponent(asset.preview || "")}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs p-2 hover:text-red-600"
            title="Pinterest"
            data-testid={`pin-${asset.kind}`}
          >
            <MoreHorizontal className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}

function CaptionModal({ asset, coupleNames, onClose }) {
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [style, setStyle] = useState("tabloid");

  // Map asset.kind to API asset_kind
  const apiKind = (
    asset.kind === "tiktok-png" || asset.kind === "tiktok-mp4" ? "tiktok-game" : asset.kind
  );

  async function generate() {
    setBusy(true);
    try {
      const { data } = await api.post("/share/caption", {
        asset_kind: apiKind,
        couple_names: coupleNames,
        title: asset.title,
        style,
      });
      setCaption(data.caption);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  function copy() {
    navigator.clipboard.writeText(caption);
    toast.success("Caption copied — paste it in your post");
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="caption-modal">
      <div className="bg-white rounded-3xl max-w-lg w-full">
        <div className="border-b border-[var(--line)] px-6 py-4 flex items-center justify-between">
          <p className="font-serif text-xl flex items-center gap-2"><Wand2 className="w-4 h-4 text-[var(--gold)]" /> AI Caption</p>
          <button onClick={onClose} className="text-[var(--muted)]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-[var(--muted)]">{asset.title}</p>

          <div className="flex gap-2">
            {[
              { key: "tabloid", label: "Locked In" },
              { key: "romantic", label: "Romantic" },
              { key: "playful", label: "Playful" },
              { key: "minimal", label: "Minimal" },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setStyle(s.key)}
                data-testid={`style-${s.key}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${style === s.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[var(--line)] text-[var(--muted)] hover:border-[var(--gold)]"}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={busy ? "Conjuring your caption…" : "Click 'Generate' to write a Claude-powered branded caption."}
            rows={8}
            data-testid="caption-output"
            className="input-luxe resize-none font-serif"
          />

          <div className="flex gap-2">
            <button onClick={generate} disabled={busy} data-testid="generate-caption" className="btn-gold flex-1">
              {busy ? "Generating…" : caption ? "Regenerate" : "Generate"}
            </button>
            {caption && (
              <button onClick={copy} className="btn-outline-gold text-xs" data-testid="copy-caption">
                <Copy className="w-3 h-3" /> Copy
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
